import {
  Avatar,
  Chip,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

const methodConfig = {
  email: {
    label: "Email",
    color: "#b0951c",
    bg: "#FCD52830",
    icon: <EmailOutlinedIcon sx={{ fontSize: 14 }} />,
  },
  google: {
    label: "Google",
    color: "#4285F4",
    bg: "#4285F415",
    icon: null,
  },
  facebook: {
    label: "Facebook",
    color: "#1877F2",
    bg: "#1877F215",
    icon: null,
  },
};

function formatDate(dateString) {
  if (!dateString) return "";
  return new Date(dateString).toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function timeAgo(dateString) {
  if (!dateString) return "";
  const seconds = Math.floor((new Date() - new Date(dateString)) / 1000);
  if (seconds < 60) return "Justo ahora";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `Hace ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Hace ${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `Hace ${days}d`;
  const months = Math.floor(days / 30);
  if (months < 12) return `Hace ${months} mes${months > 1 ? "es" : ""}`;
  const years = Math.floor(months / 12);
  return `Hace ${years} año${years > 1 ? "s" : ""}`;
}

function getInitials(username) {
  if (!username) return "?";
  return username.split("@")[0].substring(0, 2).toUpperCase();
}

const headCellSx = {
  fontWeight: 700,
  color: "#161616",
  borderBottom: "none",
  py: 1.8,
  fontSize: "0.82rem",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  backgroundColor: "#f5f5f5",
  position: "sticky",
  top: 0,
  zIndex: 2,
};

export default function LogsTable({ logs, loading }) {
  return (
    <TableContainer sx={{ maxHeight: { xs: "50vh", md: "60vh" }, overflowY: "auto" }}>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={headCellSx}>Usuario</TableCell>
            <TableCell sx={headCellSx}>Método</TableCell>
            <TableCell sx={headCellSx}>Fecha</TableCell>
            <TableCell sx={headCellSx} align="right">
              Tiempo
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading
            ? [...Array(6)].map((_, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      <Skeleton
                        variant="circular"
                        width={36}
                        height={36}
                      />
                      <Skeleton width={160} height={20} />
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Skeleton
                      width={70}
                      height={28}
                      sx={{ borderRadius: 2 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Skeleton width={140} height={20} />
                  </TableCell>
                  <TableCell align="right">
                    <Skeleton
                      width={80}
                      height={20}
                      sx={{ ml: "auto" }}
                    />
                  </TableCell>
                </TableRow>
              ))
            : logs.length === 0
            ? (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    sx={{
                      textAlign: "center",
                      py: 6,
                      borderBottom: "none",
                    }}
                  >
                    <Stack alignItems="center" spacing={1}>
                      <Typography color="text.secondary" variant="body2">
                        No se encontraron registros de acceso
                      </Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              )
            : logs.map((log, index) => {
                const method = methodConfig[log.method] || {
                  label: log.method || "—",
                  color: "#888",
                  bg: "#f5f5f5",
                  icon: null,
                };
                return (
                  <TableRow
                    key={log.id}
                    sx={{
                      bgcolor: index % 2 === 0 ? "#fff" : "#fafafa",
                      "&:hover": { bgcolor: "#FCD52810" },
                      transition: "background-color 0.2s",
                      "& td": { borderColor: "#f0f0f0", py: 1.3 },
                    }}
                  >
                    <TableCell>
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1.5}
                      >
                        <Avatar
                          sx={{
                            width: 36,
                            height: 36,
                            bgcolor: "#FCD52830",
                            color: "#b0951c",
                            fontSize: "0.8rem",
                            fontWeight: 700,
                          }}
                        >
                          {getInitials(log.username)}
                        </Avatar>
                        <Typography
                          variant="body2"
                          fontWeight={500}
                          sx={{
                            maxWidth: 240,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {log.username}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={method.icon}
                        label={method.label}
                        size="small"
                        sx={{
                          bgcolor: method.bg,
                          color: method.color,
                          fontWeight: 600,
                          fontSize: "0.75rem",
                          borderRadius: 1.5,
                          "& .MuiChip-icon": { color: method.color },
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(log.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={formatDate(log.createdAt)} arrow>
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="flex-end"
                          spacing={0.5}
                        >
                          <AccessTimeIcon
                            sx={{ fontSize: 14, color: "#aaa" }}
                          />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                          >
                            {timeAgo(log.createdAt)}
                          </Typography>
                        </Stack>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
